import { contractRead, DEFAULT_PREC } from '@/shared/lib'
import { Badge, Button, CardContent, CardTitle } from '@/shared/ui'
import { useEffect, useState } from 'react'
import CreateNpo from './create-npo'
import useRequest from '@/shared/lib/use-request'
import { IUser } from '@/entities/user/model/types'
import { UserCard } from '@/entities/user'
import LoadingIcon from '@/shared/ui/loading-icon'
import { LangStore } from '@/shared/model/store'
import { useAccount } from 'wagmi'
import { AmountBlock } from '@/shared/ui/amount-block/amount-block'
import { publicClientWeb3 } from '@/app/model'

interface Props {
	npo: IUser
	setTab?: (tab: string) => void
}

const MainNpo = ({ npo, setTab }: Props) => {
	const { langPack } = LangStore()
	const { fetch, result, isLoading: isLoadingMembers } = useRequest<IUser[]>('npoMembers')
	const [admins, setAdmins] = useState<`0x${string}`[]>([])
	const account = useAccount()
	const [balances, setBalances] = useState({
		savva: 0n,
		pls: 0n,
		nftGain: 0n,
		staked: 0n
	})
	const { address: wagmiAdress } = useAccount()

	const readIsAdmin = async () => {
		const res = await contractRead('SavvaNPO', 'getAdmins', [], npo?.address)
		setAdmins(res)
	}

	const updateBalance = async () => {
		const [tokensAmount, staked, pls] = await Promise.all([
			contractRead('SavvaToken', 'balanceOf', [npo?.address]),
			contractRead('Staking', 'balanceOf', [npo?.address]),
			publicClientWeb3().getBalance({ address: npo?.address })
		])

		setBalances(prev => ({ ...prev, savva: tokensAmount, pls, staked }))
	}

	const fetchMembers = () => {
		fetch({ params: { npo_addr: npo?.address } })
		readIsAdmin()
	}

	useEffect(() => {
		if (!npo.address) return
		fetchMembers()
		updateBalance()
	}, [npo.address])

	if (!npo.address || !result) return <CreateNpo />

	const isAdmin = wagmiAdress ? admins.includes(wagmiAdress) : false
	const nativeCurrencySymbol = account?.chain?.nativeCurrency.symbol || 'PLS'

	const stats = [
		{
			label: langPack('profile.sponsor_other'),
			value: npo.total_sponsoring,
			showPerUsd: true
		},
		{
			label: langPack('profile.sponsors_users'),
			value: npo.total_sponsored,
			showPerUsd: true
		},
		{
			label: langPack('profile.followings'),
			value: npo.n_following * DEFAULT_PREC,
			withoutIcon: true
		},
		{
			label: langPack('profile.followers'),
			value: npo.n_followers * DEFAULT_PREC,
			withoutIcon: true
		}
	]

	const walletItems = [
		{
			label: 'SAVVA',
			amount: balances.savva,
			token: 'SAVVA'
		},
		{
			label: langPack('profile.wallet_staked'),
			amount: balances.staked,
			token: 'SAVVA'
		},
		{
			label: nativeCurrencySymbol,
			amount: balances.pls,
			token: nativeCurrencySymbol
		}
	]

	return (
		<div className='flex w-full flex-col'>
			{/* Stats Section */}
			<div className='mb-4 flex gap-4'>
				{stats.map((stat, index) => (
					<div key={index} className='flex w-full flex-col items-center justify-between gap-2 rounded-lg p-4 shadow-lg'>
						<div className='text-[grey]'>{stat.label}</div>
						<AmountBlock amount={stat.value} size='md' withoutIcon={stat.withoutIcon} />
					</div>
				))}
			</div>

			{/* Main Content */}
			<div className='flex w-full flex-row flex-wrap justify-between gap-4'>
				{/* Members Section */}
				<div className='w-full rounded-lg bg-background p-4 shadow-lg md:w-[49%]'>
					<CardTitle className='flex justify-between border-b-2 py-3'>
						<span>{langPack('npo.members')}</span>
						{isAdmin && (
							<Button onClick={() => setTab?.('members')} variant='link'>
								{langPack('npo.manage')}
							</Button>
						)}
					</CardTitle>
					<CardContent className='mt-4 flex flex-col gap-4'>
						{isLoadingMembers ? (
							<LoadingIcon />
						) : (
							result?.map(member => (
								<div key={member.address} className='flex w-full items-center gap-4'>
									<UserCard user={member} length={30} />
									{member.confirmed && isAdmin && (
										<Badge variant='success' className='h-[25px]'>
											{langPack('npo.confirmed')}
										</Badge>
									)}
									{!member.confirmed && isAdmin && (
										<Badge variant='destructive' className='h-[25px]'>
											{langPack('npo.unconfirmed')}
										</Badge>
									)}
									{admins.includes(member.address) && (
										<Badge variant='orange' className='h-[25px]'>
											{langPack('npo.admin')}
										</Badge>
									)}
								</div>
							))
						)}
					</CardContent>
				</div>

				{/* Wallet Section */}
				<div className='w-full rounded-lg p-4 shadow-lg md:w-[49%]'>
					<CardTitle className='flex justify-between border-b-2 py-3'>
						<span>{langPack('profile.wallet_balances')}</span>
					</CardTitle>
					<div className='mt-4 flex flex-col gap-4'>
						{walletItems.map((item, index) => (
							<div key={index} className='flex flex-row items-center justify-between'>
								<div className='text-[20px] font-bold'>{item.label}</div>
								<AmountBlock
									amount={item.amount}
									size='md'
									perUsd
									token={item.token}
									usdClassName='absolute -bottom-[10px] text-[grey] text-[13px]'
								/>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}

export default MainNpo
